import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  isEmpty,
  trim,
  throttle,
  toNumber,
  map,
  values,
  some,
  debounce,
  filter,
  each,
  includes,
} from "lodash";
import Textarea from "react-textarea-autosize";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlaneTop } from "@fortawesome/pro-solid-svg-icons";

import {
  getComposerMessageToQuoteId,
  getComposerMessageToEditId,
  getComposerFileAttachments,
  getComposerImageAttachments,
  getComposerLinkAttachments,
} from "selectors/chat/composer";
import { getCanForChat } from "selectors/chat/chat";
import { getMessageById } from "selectors/chat/messages";
import MessageToQuote from "./MessageToQuote";
import { useForm } from "react-hook-form";
import usePersistentState from "hooks/usePersistentSate";
import EditMessageHint from "components/chat/ChatRoom/MessageComposer/EditMessageHint";
import { isAfter, subMinutes } from "date-fns";
import { faCheck, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { faImagesCirclePlus, faFilesCirclePlus } from "icons/customIcons";
import FileAttachments from "components/chat/ChatRoom/MessageComposer/FileAttachments";
import ImageAttachments from "components/chat/ChatRoom/MessageComposer/ImageAttachments";
import FileUploadModal from "components/chat/ChatRoom/MessageComposer/FileUploadModal";
import DropDown from "components/shared/DropDown";
import { DragDropContext } from "components/chat/ChatRoom";
import useLinkCrawler from "../../../../hooks/useLinkCrawler";
import LinkAttachmentPreview from "components/chat/ChatRoom/MessageComposer/LinkAttachmentPreview";

type FormData = { text: string };

export default function MessageComposer({ chatId }: { chatId: string }) {
  const { setIsModalOpen } = useContext(DragDropContext);
  const dispatch = useDispatch();
  const messageToQuoteId = useSelector((state) =>
    getComposerMessageToQuoteId(state, { chatId }),
  );
  const messageToEditId = useSelector((state) =>
    getComposerMessageToEditId(state, { chatId }),
  );
  const messageToEdit = useSelector((state) =>
    getMessageById(state, { id: messageToEditId, chatId }),
  );
  const fileAttachments = useSelector((state) =>
    getComposerFileAttachments(state, { chatId }),
  );
  const imageAttachments = useSelector((state) =>
    getComposerImageAttachments(state, { chatId }),
  );
  const linkAttachments = useSelector((state) =>
    getComposerLinkAttachments(state, { chatId }),
  );
  const updateTimePeriod =
    Preload.current_network.config.chat.message.update_period_of_time;

  const [composerTextByChatId, setComposerTextByChatId] = usePersistentState(
    "composerTextByChatId",
    {},
  );

  const [showFileUpload, setShowFileUpload] = useState(false);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [showCrawlerError, setShowCrawlerError] = useState(false);

  const { mutate: crawlUrl } = useLinkCrawler({
    onSuccess: (link, url: string) => {
      console.log("Crawled link:", link, url);
      dispatch({
        type: "chat/COMPOSER/ADD_LINK_ATTACHMENT",
        chatId,
        linkAttachment: {
          ...link,
          image_url: link.images?.[0],
          url, // set url cause it's not set for intern links
          loading: false,
        },
      });
    },
    onError: (error) => {
      console.log("Error while crawling link:", error);
      setShowCrawlerError(true);
    },
  });

  const { handleSubmit, register, getValues, setFocus, setValue, reset } =
    useForm<FormData>();

  const invokeDebounced = useMemo(
    () =>
      throttle((text) => {
        dispatch({ type: "chat/COMPOSER/SET_TEXT", chatId, text });
      }, 1000),
    [chatId],
  );

  const isUploadingAttachments = some(
    [...(imageAttachments || []), ...(fileAttachments || [])],
    { state: "uploading" },
  );

  const maxFileAttachments =
    Preload.current_network.config.chat.message.max_file_attachments;
  const maxImageAttachments =
    Preload.current_network.config.chat.message.max_image_attachments;

  useEffect(() => {
    if (messageToEditId && messageToEdit) {
      setValue("text", messageToEdit.text);
      setComposerTextByChatId({
        ...composerTextByChatId,
        [chatId]: messageToEdit.text,
      });
    } else {
      setComposerTextByChatId({
        ...composerTextByChatId,
        [chatId]: "",
      });
      setValue("text", "");
    }
  }, [messageToEdit, messageToEditId]);

  function isValidURL(urlString: string) {
    try {
      new URL(urlString);
      return true;
    } catch (_) {
      return false;
    }
  }

  const debounceCrawl = useCallback(
    debounce((urls) => {
      each(urls, (url) => {
        if (isValidURL(url)) {
          crawlUrl(url);
        }
      });
    }, 300),
    [crawlUrl],
  );

  const urlRegex =
    /\bhttps?:\/\/(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?::\d+)?(?:\/[^\s]*)?\b/g;

  function changeText(e: { target: { value: string } }) {
    const text = e.target.value;

    const urls = text.match(urlRegex);
    if (urls && urls.length > 0) {
      const firstUrl = urls[0];

      const selectedUrls = map(linkAttachments, "url");
      const newUrls = filter(urls, (url) => !includes(selectedUrls, url));

      dispatch({
        type: "chat/COMPOSER/ADD_LINK_ATTACHMENTS",
        chatId,
        linkAttachments: map(newUrls, (url, index) => ({
          url,
          loading: true,
          show: includes(newUrls, firstUrl) && index === 0,
        })),
      });

      debounceCrawl(newUrls);

      dispatch({
        type: "chat/COMPOSER/CLEAN_LINK_ATTACHMENTS",
        chatId,
        urls,
      });
    } else {
      dispatch({
        type: "chat/COMPOSER/CLEAN_LINK_ATTACHMENTS",
        chatId,
      });
    }

    setComposerTextByChatId({
      ...composerTextByChatId,
      [chatId]: text,
    });
    invokeDebounced(text);
  }

  function submit({ text }: FormData) {
    if (
      (isEmpty(trim(text)) &&
        isEmpty(fileAttachments) &&
        isEmpty(imageAttachments)) ||
      isUploadingAttachments
    ) {
      return;
    }

    const payload = {
      text,
      quoted_message_id: messageToQuoteId,
      file_ids: map(fileAttachments, "id"),
      image_ids: map(imageAttachments, "id"),
      link_attachments: values(linkAttachments),
    };

    if (messageToEditId && messageToEdit) {
      if (
        isAfter(
          new Date(messageToEdit.created_at),
          subMinutes(new Date(), toNumber(updateTimePeriod)),
        )
      ) {
        payload["id"] = messageToEditId;
      } else {
        dispatch({
          type: "chat/COMPOSER/SET_MESSAGE_TO_EDIT",
          messageId: null,
          chatId,
        });
        toastr.error(
          I18n.t("js.chat.composer.edit.update_period_of_time_ended", {
            minutes: updateTimePeriod,
          }),
        );
        return;
      }
    }

    dispatch({
      type: "chat/SEND_MESSAGE",
      meta: { chatId },
      payload,
    });

    setComposerTextByChatId({ ...composerTextByChatId, [chatId]: "" });
    reset();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter") {
      if (!e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
        handleSubmit(submit)(e);
      }
    }
  }

  useEffect(() => {
    setFocus("text");
  }, [chatId, messageToQuoteId, messageToEditId]);

  useEffect(() => {
    setValue("text", composerTextByChatId[chatId]);
  }, [chatId]);

  const can = useSelector((state) => getCanForChat(state, { chatId }));
  const disabled = can && !can.write; // optimistically enable if we haven't received permissions yet

  function addFileAttachments(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();

    if (
      fileAttachments &&
      values(fileAttachments).length === maxFileAttachments
    ) {
      toastr.error(
        I18n.t("js.chat.composer.max_files_reached", {
          max_files: maxFileAttachments,
        }),
      );
    } else {
      setShowFileUpload(true);
      setIsModalOpen(true);
    }
  }

  function addImageAttachments(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();

    if (
      imageAttachments &&
      values(imageAttachments).length === maxImageAttachments
    ) {
      toastr.error(
        I18n.t("js.chat.composer.max_images_reached", {
          max_files: maxImageAttachments,
        }),
      );
    } else {
      setShowImageUpload(true);
      setIsModalOpen(true);
    }
  }

  return (
    <div className="chat__message_composer">
      {showFileUpload && (
        <FileUploadModal
          chatId={chatId}
          selectedFileAttachmentsCount={values(fileAttachments).length}
          close={() => {
            setShowFileUpload(false);
            setIsModalOpen(false);
          }}
          maxFiles={maxFileAttachments}
        />
      )}
      {showImageUpload && (
        <FileUploadModal
          chatId={chatId}
          selectedFileAttachmentsCount={values(imageAttachments).length}
          close={() => {
            setShowImageUpload(false);
            setIsModalOpen(false);
          }}
          maxFiles={maxImageAttachments}
          isImageUploader
        />
      )}
      {messageToEditId && messageToEdit && (
        <EditMessageHint chatId={chatId} text={messageToEdit.text} />
      )}
      <MessageToQuote id={messageToQuoteId} chatId={chatId} />
      {!isEmpty(fileAttachments) && (
        <FileAttachments files={fileAttachments} chatId={chatId} />
      )}
      {!isEmpty(imageAttachments) && (
        <ImageAttachments images={imageAttachments} chatId={chatId} />
      )}
      {!isEmpty(linkAttachments) && linkAttachments[0].show && (
        <LinkAttachmentPreview
          link={linkAttachments[0]}
          chatId={chatId}
          showCrawlerError={showCrawlerError}
        />
      )}
      <form onSubmit={handleSubmit(submit)} className="flex items-end gap-2">
        <div className="grid stack-container w-full">
          <div className="flex items-end dropup stack-item w-fit self-end pl-2 pb-1">
            <DropDown.Toggle
              buttonType="normal"
              className="p-0"
              showIcon={false}
            >
              <FontAwesomeIcon className="text-muted" icon={faPlus} />
            </DropDown.Toggle>
            <DropDown>
              <DropDown.Item
                icon={faFilesCirclePlus}
                text={I18n.t("js.chat.composer.add_files_label")}
                onSelect={addFileAttachments}
              />
              <DropDown.Item
                icon={faImagesCirclePlus}
                text={I18n.t("js.chat.composer.add_images_label")}
                onSelect={addImageAttachments}
              />
            </DropDown>
          </div>
          <Textarea
            {...register("text", {
              onChange: changeText,
            })}
            minRows={1}
            maxRows={8}
            //placeholder={
            //  disabled
            //    ? I18n.t("js.chat.composer.disabled")
            //    : I18n.t("js.chat.composer.placeholder")
            //}
            onKeyDown={onKeyDown}
            autoFocus
            className="chat__message-composer stack-item"
            disabled={disabled}
          />
          <button
            className="stack-item chat__message-composer_action btn btn-primary btn-sm"
            type="submit"
            disabled={
              (!trim(getValues("text")) &&
                isEmpty(fileAttachments) &&
                isEmpty(imageAttachments)) ||
              isUploadingAttachments
            }
          >
            <FontAwesomeIcon
              icon={messageToEditId ? faCheck : faPaperPlaneTop}
            />
          </button>
        </div>
      </form>
    </div>
  );
}
